import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get, request } from 'utils/api';

const { getSubscription, getSubscriptionByProduct } = API.END_POINTS.API;

import type {
  Action,
  Dispatch,
  Subscription,
  SubscriptionProduct,
  SubscriptionProductAgreements,
  SubscriptionRevenueSource,
  ThunkAction
} from 'types';

const RESET = 'subscription/RESET';
const RECEIVE = 'subscription/RECEIVE';
const RECEIVE_BY_PRODUCT = 'subscription/RECEIVE_BY_PRODUCT';
const RECEIVE_SUBSCRIPTION_PRODUCT = 'subscription/RECEIVE_SUBSCRIPTION_PRODUCT';
const RECEIVE_TYPE = 'subscription/RECEIVE_TYPE';
const LOADING = 'subscription/LOADING';
// TODO remove all related to PREVENT_CHECK because this is just temporal, we are waiting
// for the backend to finish what happened if the organization don't have subscription
const PREVENT_CHECK = 'subscription/PREVENT_CHECK';

const initialState = {
  subscriptions: {
    cm: [],
    crm: [],
    insights: []
  },
  product: {
    type: '',
    revenue: 'external',
    agreements: [] as Array<SubscriptionProduct>,
    productAgreements: {} as SubscriptionProductAgreements
  },
  isLoading: false,
  preventCheck: false
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

function setPreventCheck(): Action {
  return {
    type: PREVENT_CHECK
  };
}

function receiveSubscription(subscription: Subscription): Action {
  return {
    type: RECEIVE,
    payload: {
      subscription
    }
  };
}

function receiveSubscriptionType(type: string): Action {
  return {
    type: RECEIVE_TYPE,
    payload: {
      type
    }
  };
}

function receiveSubscriptionProduct(product: string, data): Action {
  return {
    type: RECEIVE_SUBSCRIPTION_PRODUCT,
    payload: {
      product,
      data
    }
  };
}

function receiveSubscriptionByProduct(productName, permissions): Action {
  return {
    type: RECEIVE_BY_PRODUCT,
    payload: {
      productName,
      permissions
    }
  };
}

export function resetSubscription(): Action {
  return {
    type: RESET
  };
}

export function fetchSubscriptionByOrganizationId(organizationId: number): ThunkAction {
  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    const url: string = getSubscription(organizationId);

    return get(url)
      .then(response => {
        dispatch(receiveSubscription(response.data));
        dispatch(setLoadingStatus(false));
      })
      .catch(() => {
        dispatch(setLoadingStatus(false));
        dispatch(setPreventCheck());
      });
  };
}

export function fetchSubscriptionByProductAndOrganizationId(productName: string, organizationId: number): ThunkAction {
  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    const url: string = getSubscriptionByProduct(organizationId, productName);

    return get(url)
      .then(response => dispatch(receiveSubscriptionByProduct(productName, response.data.productAgreements[productName].capabilities)))
      .catch(() => {
        // logError(error);
        dispatch(setLoadingStatus(false));
        dispatch(setPreventCheck());
      });
  };
}

export function changeSubscriptionType(
  organizationId: number,
  type: string,
  revenue: SubscriptionRevenueSource
): ThunkAction {
  return (dispatch: Dispatch) => {
    const url = getSubscription(organizationId);
    dispatch(setLoadingStatus(true));
    dispatch(showLoading(url));

    return request('PUT', url, { data: { type, revenue } })
      .then(() => {
        dispatch(receiveSubscriptionType(type));
        dispatch(hideLoading(url));
        dispatch(fetchSubscriptionByOrganizationId(Number(organizationId)));
      })
      .catch(error => {
        dispatch(setLoadingStatus(false));
        dispatch(hideLoading(url));
        throw error;
      });
  };
}

export function changeSubscriptionProducts(organizationId: number, products: {}): ThunkAction {
  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    const patchRequests = Object.entries(products)
      .map(([productType, product]) => {
        const url = `${getSubscription(organizationId)}?product=${productType}`;
        dispatch(showLoading(url));

        return request('PATCH', url, { data: product })
          .then(() => {
            dispatch(receiveSubscriptionProduct(productType, product));
            dispatch(hideLoading(url));
          })
          .catch(error => {
            dispatch(hideLoading(url));
            throw error;
          });
      });

    return Promise.all(patchRequests)
      .then(() => dispatch(setLoadingStatus(false)))
      .catch(error => {
        dispatch(setLoadingStatus(false));
        throw error;
      });
  };
}

export default createReducer<any, any>('subscription', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    preventCheck: false,
    isLoading: status
  }),
  [PREVENT_CHECK]: (state) => ({
    ...state,
    preventCheck: true
  }),
  [RECEIVE]: (state, { subscription }) => ({
    ...state,
    isLoading: false,
    product: {
      type: subscription.type,
      // COLLAB-13203: all organisations have `external` source of review by default.
      revenue: subscription?.revenue ?? 'external',
      agreements: Object.keys(subscription.productAgreements).map(key => key) as Array<SubscriptionProduct>,
      productAgreements: subscription.productAgreements
    }
  }),
  [RECEIVE_TYPE]: (state, { type }) => ({
    ...state,
    isLoading: false,
    product: {
      ...state.product,
      type
    }
  }),
  [RECEIVE_SUBSCRIPTION_PRODUCT]: (state, { product, data }) => ({
    ...state,
    isLoading: false,
    product: {
      ...state.product,
      productAgreements: {
        ...state.product.productAgreements,
        [product]: data
      }
    }
  }),
  [RECEIVE_BY_PRODUCT]: (state, { productName, permissions }) => ({
    ...state,
    preventCheck: false,
    isLoading: false,
    subscriptions: {
      ...state.subscriptions,
      [productName]: [
        ...permissions
      ]
    }
  })
});
